/**
 * Admin Portal → Partners → one partner — `/admin/partners/:partnerId`.
 *
 * Tabs: Overview (the seven-stage funnel, iPhone/Android, per tag, with its honesty
 * labels), Links & tags (tag detail: links, offer, bounties, clipboard, codes, the website code
 * and the partner email kit), Contacts (the partner's opted-in people: import with the consent
 * attestation, the contacts funnel, test send then Send, resend, erase, withdrawals — partner
 * launch UC1), Codes (every code of the partner), API keys (the partner API's keys: create —
 * shown once —, rotate, revoke; codes item C6; the contacts scopes are opt-in), Statements
 * (statements, draft, adjustment, ledger) and Settings (the co-branded page with the Partner
 * page link, the record, pause / end — ended is final).
 * Spec: docs/handoff/PARTNERS_ADMIN_FE_HANDOFF.md §2.3–§2.6; docs/plans/PARTNER_LAUNCH_PLAN_2026_10_17.md.
 *
 * Web-only (the admin SPA): react-router is fine here.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AdminButton, AdminCard, SectionHeader } from '../../../components/admin/ui';
import { PartnersAdmin } from '../../../services/partnersAdminService';
import {
  FUNNEL_STAGES,
  FUNNEL_STAGE_LABELS,
  FUNNEL_TOOLTIPS,
  accountsWithoutClickText,
  formatCents,
  funnelByTag,
  partnerFormFromRecord,
  partnerPatchBody,
  stageConversions,
  statusMoveCopy,
  statusBadge,
  statusMoves,
  type Funnel,
  type LinkRow,
  type PartnerForm,
  type PartnerRecord,
  type PartnerStatus,
  type TagRow,
} from './partnersLogic';
import { PartnerTagPanel } from './PartnerTagPanel';
import { partnerDetailTabs, type PartnerDetailTab } from '../codes/codesAdminLogic';
import { PartnerCodesPanel } from './PartnerCodesPanel';
import { PartnerStatementsPanel } from './PartnerStatementsPanel';
import { PartnerApiKeysPanel } from './PartnerApiKeysPanel';
import { PartnerContactsPanel } from './PartnerContactsPanel';
import { PartnerLandingCard } from './PartnerLandingCard';
import { CODES_ROUTES } from '../codes/codesAreaLogic';
import { CodeCatalogPanel } from '../codes/CodeCatalogPanel';
import { ApiKeysSummary } from '../codes/ApiKeysSummary';
import {
  NoticeLine,
  ProgramBanner,
  ReadOnlyNote,
  StatusBadge,
  TypedConfirm,
  errText,
  hint,
  input,
  label,
  row,
  td,
  th,
  useAdminRole,
  useBusy,
  useProgramStatus,
  type Notice,
} from './PartnersShared';

type Tab = PartnerDetailTab;

/**
 * `partnerId` given (the "Coach Cedar codes" page, web/src/pages/admin/codes/AdminHouseCodes.tsx)
 * overrides the route's. The Coach Cedar house partner (migration 1876) opens in HOUSE MODE:
 * Overview, Links & tags and Codes only — it has no API keys, statements or editable settings —
 * and its tags show no bounty (a house tag pays none).
 */
export function AdminPartnerDetail({ partnerId: fixedPartnerId }: { partnerId?: string } = {}) {
  const params = useParams();
  const partnerId = fixedPartnerId ?? params.partnerId ?? '';
  const [search, setSearch] = useSearchParams();
  const { role, writable, loaded: roleLoaded, email: adminEmail } = useAdminRole();
  const program = useProgramStatus();
  const [partner, setPartner] = useState<PartnerRecord | null>(null);
  const [tags, setTags] = useState<TagRow[]>([]);
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [notice, setNotice] = useState<Notice>(null);
  const tagParam = search.get('tag');
  const house = !!partner?.is_house;
  const tabs = partnerDetailTabs(house);
  const asked = ((search.get('tab') as Tab) || (tagParam ? 'tags' : 'overview'));
  // A tab the house does not have (a stale link) falls back to its Overview.
  const tab: Tab = tabs.some(([t]) => t === asked) ? asked : 'overview';

  const setTab = (t: Tab) => {
    const next = new URLSearchParams(search);
    next.set('tab', t);
    setSearch(next, { replace: true });
  };
  const selectTag = (id: string | null) => {
    const next = new URLSearchParams(search);
    next.set('tab', 'tags');
    if (id) next.set('tag', id); else next.delete('tag');
    setSearch(next, { replace: true });
  };

  const load = useCallback(async () => {
    try {
      const res = await PartnersAdmin.getPartner(partnerId);
      setPartner(res.partner);
      setTags(res.tags || []);
      setLinks(res.links || []);
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    }
  }, [partnerId]);

  useEffect(() => { load(); }, [load]);

  const endedTerminal = program.status?.ended_terminal ?? true;
  // null = the switches could not be read: the copy says so rather than guess the rule.
  const judgedAtTap = program.status ? program.status.pause_judged_at_tap : null;

  return (
    <div style={{ padding: 24, maxWidth: 1280 }}>
      <div style={{ fontSize: 13, marginBottom: 8 }}>
        {house ? <Link to={`${CODES_ROUTES.batches}?owner=house`}>Code batches →</Link> : <Link to="/admin/partners">← Partners</Link>}
      </div>
      <SectionHeader
        title={partner ? <>{partner.display_name} <StatusBadge status={partner.status} tone={statusBadge(partner.status)} /></> : 'Partner'}
        subtitle={partner ? `${partner.code} · ${partner.legal_name}` : undefined}
      />
      <ProgramBanner status={program.status} error={program.error} />
      <ReadOnlyNote writable={writable} loaded={roleLoaded} role={role} />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      {house && (
        <div data-testid="house-mode-note" style={{ ...hint, margin: '0 0 12px' }}>
          Coach Cedar&apos;s own codes: discounts, longer free trials or tracking only, for in-house campaigns and manual
          grants. The offer sheet reads &quot;Your offer from Coach Cedar&quot;. A Coach Cedar code is the person&apos;s
          first source, and it never pays a bounty, never appears on a statement and is never minted through the partner API.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }} role="tablist">
        {tabs.map(([t, name]) => (
          <AdminButton key={t} role="tab" aria-selected={tab === t} size="sm" variant={tab === t ? 'primary' : 'secondary'} onClick={() => setTab(t)}>
            {name}
          </AdminButton>
        ))}
      </div>

      {partner && tab === 'overview' && <OverviewTab partnerId={partnerId} tags={tags} />}
      {partner && tab === 'tags' && (
        <PartnerTagPanel
          partnerId={partnerId}
          partnerCode={partner.code}
          partnerStatus={partner.status}
          judgedAtTap={judgedAtTap}
          tags={tags}
          links={links}
          selectedTagId={tagParam}
          onSelect={selectTag}
          writable={writable}
          endedTerminal={endedTerminal}
          onChanged={load}
          house={house}
          partnerName={partner.display_name}
        />
      )}
      {partner && tab === 'contacts' && (
        <PartnerContactsPanel key={partnerId} partner={partner} tags={tags} writable={writable} adminEmail={adminEmail}
          roleLoaded={roleLoaded} />
      )}
      {partner && tab === 'codes' && (
        <PartnerCodesTab partnerId={partnerId} tags={tags} writable={writable} roleLoaded={roleLoaded}
          view={search.get('view') === 'all' ? 'all' : 'manage'}
          onView={(v) => { const next = new URLSearchParams(search); if (v === 'all') next.set('view', 'all'); else next.delete('view'); setSearch(next, { replace: true }); }} />
      )}
      {partner && tab === 'api' && (
        <>
          {/* The Codes area's API screen, pre-filtered to this partner: each key's mints against its quota. */}
          <ApiKeysSummary partnerId={partnerId} compact />
          <PartnerApiKeysPanel key={partnerId} partnerId={partnerId} partnerEnded={partner.status === 'ended'} writable={writable} />
        </>
      )}
      {partner && tab === 'statements' && <PartnerStatementsPanel partnerId={partnerId} tags={tags} writable={writable} />}
      {partner && tab === 'settings' && (
        <SettingsTab partner={partner} tags={tags} writable={writable} endedTerminal={endedTerminal} judgedAtTap={judgedAtTap} onChanged={load} />
      )}
    </div>
  );
}

// ── Codes: this partner's codes, managed here (the partner program's words) or read in the Codes area's words ──

const PartnerCodesTab: React.FC<{
  partnerId: string; tags: TagRow[]; writable: boolean; roleLoaded: boolean;
  view: 'manage' | 'all'; onView: (v: 'manage' | 'all') => void;
}> = ({ partnerId, tags, writable, roleLoaded, view, onView }) => (
  <div>
    <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <AdminButton role="tab" size="sm" aria-selected={view === 'manage'} variant={view === 'manage' ? 'primary' : 'secondary'}
        onClick={() => onView('manage')}>Manage codes</AdminButton>
      <AdminButton role="tab" size="sm" aria-selected={view === 'all'} variant={view === 'all' ? 'primary' : 'secondary'}
        onClick={() => onView('all')}>As in All codes</AdminButton>
    </div>
    {view === 'manage'
      ? <PartnerCodesPanel key={partnerId} partnerId={partnerId} tags={tags} writable={writable} />
      : <CodeCatalogPanel key={partnerId} fixed={{ partner_id: partnerId }} writable={writable} roleLoaded={roleLoaded} />}
    {view === 'all' && (
      <div style={{ ...hint, marginTop: 8 }}>
        The same list as <Link to={`${CODES_ROUTES.all}?partner_id=${encodeURIComponent(partnerId)}`}>Codes → All codes</Link>, for this partner only.
      </div>
    )}
  </div>
);

// ── Overview: the funnel ─────────────────────────────────────────────────────

/**
 * The overview funnel. Accounts without a click (S1 #5418) are part of Accounts: a breakdown under
 * it, never a stage of their own, and installs → accounts counts only the accounts that came through
 * a click (stageConversions), so no share reads over 100%. Exported for its test.
 */
export const OverviewTab: React.FC<{ partnerId: string; tags: TagRow[] }> = ({ partnerId, tags }) => {
  const [days, setDays] = useState(30);
  const [tagId, setTagId] = useState('');
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    PartnersAdmin.funnel(partnerId, days, tagId || undefined).then(setFunnel).catch((e) => setError(errText(e)));
  }, [partnerId, days, tagId]);

  const byTag = useMemo(() => (funnel ? funnelByTag(funnel.rows) : []), [funnel]);
  const conv = funnel ? stageConversions(funnel.totals) : [];
  const iosInstalls = funnel ? funnel.rows.filter((r) => r.platform === 'ios').reduce((a, r) => a + Number(r.installs || 0), 0) : 0;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={row}>
        <div>
          <label style={label} htmlFor="fn-days">Window</label>
          <select id="fn-days" style={input} value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="fn-tag">Tag</label>
          <select id="fn-tag" style={input} value={tagId} onChange={(e) => setTagId(e.target.value)}>
            <option value="">All tags</option>
            {tags.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
          </select>
        </div>
        <div style={hint} title={FUNNEL_TOOLTIPS.window}>Counted by the date of the CLICK; accounts without a click: when attributed (ⓘ)</div>
      </div>
      {error && <NoticeLine notice={{ kind: 'error', text: error }} />}
      {funnel && (
        <>
          <AdminCard>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
              {FUNNEL_STAGES.map((s, i) => (
                <div key={s} data-testid={`overview-stage-${s}`} title={FUNNEL_TOOLTIPS[s] ?? funnel.notes?.[s]}>
                  <div style={hint}>{FUNNEL_STAGE_LABELS[s]}{FUNNEL_TOOLTIPS[s] ? ' ⓘ' : ''}</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{funnel.totals[s] ?? 0}</div>
                  {i > 0 && <div style={hint}>{conv[i - 1].pct == null ? '—' : `${conv[i - 1].pct}% of ${FUNNEL_STAGE_LABELS[conv[i - 1].from].toLowerCase()}`}</div>}
                  {s === 'accounts' && accountsWithoutClickText(funnel.totals.accounts_without_click) && (
                    <div style={hint} title={funnel.notes?.accounts_without_click ?? FUNNEL_TOOLTIPS.accounts_without_click}>
                      {`${accountsWithoutClickText(funnel.totals.accounts_without_click)} ⓘ`}
                    </div>
                  )}
                </div>
              ))}
              <div title={FUNNEL_TOOLTIPS.accrued}>
                <div style={hint}>Accrued ⓘ</div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCents(funnel.totals.accrued_cents)}</div>
              </div>
            </div>
          </AdminCard>
          <AdminCard>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
              <div>
                <div style={hint}>Installs by how they were captured</div>
                {Object.entries(funnel.installs_by_method).map(([k, v]) => <div key={k}>{k.replace(/_/g, ' ')}: <b>{v}</b></div>)}
                {!Object.keys(funnel.installs_by_method).length && <div>—</div>}
              </div>
              <div title={FUNNEL_TOOLTIPS.ambiguous}>
                <div style={hint}>Ambiguous iPhone check-ins (platform-wide) ⓘ</div>
                <b>{funnel.ambiguous_checkins_platform_wide}</b>
              </div>
              <div>
                <div style={hint}>iPhone installs: ours vs Apple's campaign report</div>
                <b>{iosInstalls}</b> ours · Apple: {funnel.apple_aggregate == null ? <i>enter from App Store Connect</i> : <b>{funnel.apple_aggregate}</b>}
              </div>
            </div>
          </AdminCard>
          <AdminCard flush>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Tag</th><th style={th}>Platform</th>
                  {FUNNEL_STAGES.map((s) => <th key={s} style={th}>{FUNNEL_STAGE_LABELS[s]}</th>)}
                  <th style={th}>Accrued</th>
                </tr>
              </thead>
              <tbody>
                {funnel.rows.length === 0 && <tr><td style={td} colSpan={FUNNEL_STAGES.length + 3}>No clicks or accounts in this window.</td></tr>}
                {funnel.rows.map((r) => (
                  <tr key={`${r.tag}-${r.platform}`}>
                    <td style={td}>{r.tag}</td>
                    <td style={td}>{r.platform === 'ios' ? 'iPhone' : r.platform === 'android' ? 'Android' : r.platform}</td>
                    {FUNNEL_STAGES.map((s) => (
                      <td key={s} style={td}>
                        {r[s] ?? 0}
                        {s === 'accounts' && Number(r.accounts_without_click || 0) > 0 && (
                          <div style={hint}>{`${r.accounts_without_click} without a click`}</div>
                        )}
                      </td>
                    ))}
                    <td style={td}>{formatCents(r.accrued_cents)}</td>
                  </tr>
                ))}
                {byTag.length > 1 && byTag.map((t) => (
                  <tr key={`sum-${t.tag}`} style={{ fontWeight: 600 }}>
                    <td style={td}>{t.tag}</td><td style={td}>all</td>
                    {FUNNEL_STAGES.map((s) => (
                      <td key={s} style={td}>
                        {t[s]}
                        {s === 'accounts' && t.accounts_without_click > 0 && <div style={hint}>{`${t.accounts_without_click} without a click`}</div>}
                      </td>
                    ))}
                    <td style={td}>{formatCents(t.accrued_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminCard>
        </>
      )}
    </div>
  );
};

// ── Settings: the co-branded page, the record, pause / end ───────────────────

const SettingsTab: React.FC<{
  partner: PartnerRecord; tags: TagRow[]; writable: boolean; endedTerminal: boolean; judgedAtTap: boolean | null; onChanged: () => void;
}> = ({
  partner, tags, writable, endedTerminal, judgedAtTap, onChanged,
}) => {
  const [form, setForm] = useState<PartnerForm>(partnerFormFromRecord(partner));
  const [notice, setNotice] = useState<Notice>(null);
  const [pending, setPending] = useState<PartnerStatus | null>(null);
  const [busy, run] = useBusy();
  const diff = partnerPatchBody(partner, form);
  // The co-branded page has its own card and save (PartnerLandingCard): the record never writes
  // landing_content, so a stale copy of it here can never undo a page save.
  const recordBody = Object.fromEntries(Object.entries(diff.body).filter(([k]) => k !== 'landing_content'));
  const recordErrors = Object.fromEntries(Object.entries(diff.errors).filter(([k]) => !k.startsWith('landing_')));

  useEffect(() => { setForm(partnerFormFromRecord(partner)); }, [partner]);

  const set = (k: keyof PartnerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const save = () =>
    run(async () => {
      if (Object.keys(recordErrors).length) { setNotice({ kind: 'error', text: 'Not saved:', extra: Object.values(recordErrors) }); return; }
      if (!Object.keys(recordBody).length) { setNotice({ kind: 'info', text: 'Nothing changed.' }); return; }
      try {
        await PartnersAdmin.updatePartner(partner.id, recordBody);
        setNotice({ kind: 'ok', text: `Saved: ${Object.keys(recordBody).join(', ')}.` });
        onChanged();
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const move = () =>
    run(async () => {
      if (!pending) return;
      try {
        await PartnersAdmin.setPartnerStatus(partner.id, pending);
        setNotice({ kind: 'ok', text: `The partner is now ${pending}.` });
        setPending(null);
        onChanged();
      } catch (e) {
        setPending(null);
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const field = (k: keyof PartnerForm, text: string, width = 240, type = 'text') => (
    <div style={{ minWidth: width }}>
      <label style={label} htmlFor={`st-${k}`}>{text}</label>
      <input id={`st-${k}`} type={type} style={{ ...input, width: '100%' }} disabled={!writable} value={String(form[k])} onChange={set(k)} />
      {diff.errors[k] && <div style={{ ...hint, color: 'var(--status-error-text, #991b1b)' }}>{diff.errors[k]}</div>}
    </div>
  );

  const moves = statusMoves(partner.status, endedTerminal);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Status</h4>
        <div style={row}>
          <StatusBadge status={partner.status} tone={statusBadge(partner.status)} />
          {writable && moves.map((s) => (
            <AdminButton key={s} size="sm" variant={s === 'ended' ? 'danger' : 'secondary'} onClick={() => setPending(s)} disabled={busy}>
              {s === 'active' ? (partner.status === 'ended' ? 'Reactivate' : 'Resume') : s === 'paused' ? 'Pause' : 'End partner'}
            </AdminButton>
          ))}
          {partner.status === 'ended' && endedTerminal && <span style={hint}>Ended is final — this partner cannot be reactivated.</span>}
        </div>
        <div style={hint}>
          {judgedAtTap === false
            ? 'A pause is judged when the signup runs (the tap-time rule is off): nothing attributes while paused, whenever the click was tapped.'
            : judgedAtTap === true
              ? 'A pause is judged at the tap: a click tapped while active can still earn later; one tapped while paused never does.'
              : 'Could not read which pause rule is live — check the program switches.'}
        </div>
      </AdminCard>

      {/* The co-branded page and its Partner page link (partner launch UC2): its own card and save. */}
      <PartnerLandingCard key={partner.id} partner={partner} tags={tags} writable={writable} onChanged={onChanged} />

      <AdminCard>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>The record</h4>
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={row}>{field('display_name', 'Display name')}{field('legal_name', 'Legal name (on statements)')}</div>
          <div style={row}>{field('contact_name', 'Contact name')}{field('contact_email', 'Contact email', 240, 'email')}{field('statement_email', 'Statement email (fallback: contact)', 240, 'email')}</div>
          <div style={row}>
            {field('net_days', 'Net days', 100)}
            <label style={{ fontSize: 13 }}><input type="checkbox" disabled={!writable} checked={form.tax_applies} onChange={set('tax_applies')} /> Tax applies</label>
            {field('tax_rate_pct', 'Tax rate %', 100)}
            {field('tax_number', "Partner's tax number", 180)}
          </div>
          {field('payment_method_text', 'How we pay them (prints on the statement)', 520)}
          <div>
            <label style={label} htmlFor="st-notes">Internal notes</label>
            <textarea id="st-notes" rows={3} style={{ ...input, width: '100%' }} disabled={!writable} value={form.notes} onChange={set('notes')} />
          </div>
          {writable && (
            <div><AdminButton size="sm" onClick={save} disabled={busy || !Object.keys(recordBody).length}>Save changes</AdminButton></div>
          )}
          <div style={hint}>The partner code ({partner.code}) never changes — it is in every statement number.</div>
        </div>
      </AdminCard>

      {pending && (
        <TypedConfirm
          title={pending === 'ended' ? `End ${partner.display_name}?` : `${pending === 'paused' ? 'Pause' : 'Resume'} ${partner.display_name}?`}
          body={statusMoveCopy(pending, 'partner', judgedAtTap)}
          expected={pending === 'ended' ? partner.code : pending === 'paused' ? 'PAUSE' : 'RESUME'}
          confirmLabel={pending === 'ended' ? 'End partner' : pending === 'paused' ? 'Pause' : 'Resume'}
          danger={pending === 'ended'}
          busy={busy}
          onConfirm={move}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
};

export default AdminPartnerDetail;
